<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('baseObject.name')"
          mode="view"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput mode="view" v-model="data.name" color="base" flat />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.code')"
          mode="view"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput mode="view" v-model="data.code" color="base" flat />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          mode="view"
          :name="$t('mw-params.remarks')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput mode="view" v-model="data.remarks" color="base" flat />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('mw-params.value')"
          :isHorizontalLayout="isHorizontalLayout">
          <div v-if="data.code == MWParams.COMPANY_LOGO">
            <img
              :src="'data:image/jpg;base64,' + data.defaultValue"
              alt="Image"
              class="max-w-sm max-h-16" />
            <input
              v-if="data.code === MWParams.COMPANY_LOGO"
              type="file"
              @change="handleImageUpload"
              accept="image/*"
              class="text-sm text-gray-500" />
          </div>
          <BaseToggle
            v-else-if="data.mwDataType?.name === MWDataTypes.BOOLEAN"
            name="isActive"
            v-model="valueBoolean" />
          <BaseSelectDropdown
            v-else-if="isDropdown()"
            :items="dropdownItems"
            :selected-item="selectedItem"
            :clearable="data.isNullAllowed"
            @update:selected="handleSelection"
            name="valueSelectDropdown"
            :error="valueErrorMessage" />
          <BaseInput
            v-else
            :mode="mode"
            v-model="data.defaultValue"
            :type="isNumber() ? 'number' : 'text'"
            color="base"
            flat
            :error="valueErrorMessage" />
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import type { MWParam } from '@/types/MW';
import { onMounted, ref, watch } from 'vue';
import type { SelectDropdownItem } from '@/types/Base';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import { MWDataTypes, MWParams } from '@/utils/constants';

defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  valueErrorMessage: string;
}>();

const { getRootGroupItemsLookup } = useLookupsStore();
const { dropdownrootGroupItems } = storeToRefs(useLookupsStore());

const data = defineModel<MWParam>();
const valueBoolean = ref(false);

const dropdownItems = ref<SelectDropdownItem[]>([]);
const selectedItem = ref<SelectDropdownItem | null>(null);

onMounted(async () => {
  await initializeValues();
});

watch(
  () => valueBoolean.value,
  (newData) => {
    if (data.value) {
      if (newData) data.value.defaultValue = '1';
      else data.value.defaultValue = '0';
    }
  }
);

async function initializeValues() {
  if (data.value?.mwDataType?.name === MWDataTypes.BOOLEAN) {
    valueBoolean.value = data.value?.defaultValue == '1';
  } else if (data.value?.code === MWParams.STANDARDJOBSROOTGROUP) {
    await getRootGroupItemsLookup();
    dropdownItems.value = dropdownrootGroupItems.value;
    selectedItem.value =
      dropdownrootGroupItems.value.find((item) => item.value === data.value?.defaultValue) || null;
  } else if (data.value?.remarks === '0 = Portrait, 1 = Landscape') {
    dropdownItems.value = [
      { value: '0', name: 'Portrait' },
      { value: '1', name: 'Landscape' },
    ];
    selectedItem.value =
      dropdownItems.value.find((item) => item.value === data.value?.defaultValue) ||
      dropdownItems.value[0];
  } else if (
    data.value?.mwDataType?.name === MWDataTypes.LIST_SINGLE &&
    data.value?.mwParamListValues
  ) {
    dropdownItems.value = data.value?.mwParamListValues?.map((item) => ({
      value: item.listValue,
      name: item.name,
    }));
    selectedItem.value =
      dropdownItems.value.find((item) => item.value === data.value?.defaultValue) || null;
  }
}

function handleSelection(item: SelectDropdownItem) {
  if (data.value) {
    if (item) {
      selectedItem.value = item;
      data.value.defaultValue = item.value.toString();
      data.value.defaultValueString = item.name;
    } else {
      data.value.defaultValue = '';
      data.value.defaultValueString = '';
    }
  }
}

function handleImageUpload(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = () => {
      if (data.value && typeof reader.result === 'string') {
        data.value.defaultValue = reader.result.split(',')[1];
      }
    };
    reader.readAsDataURL(file);
  }
}

function isDropdown() {
  if (data.value)
    return (
      data.value.code == MWParams.STANDARDJOBSROOTGROUP ||
      data.value.remarks === '0 = Portrait, 1 = Landscape' ||
      data.value.mwDataType?.name === MWDataTypes.LIST_SINGLE
    );
  return false;
}

function isNumber() {
  if (data.value)
    return (
      data.value.mwDataType?.name === MWDataTypes.INT ||
      data.value.mwDataType?.name === MWDataTypes.INTN ||
      data.value.mwDataType?.name === MWDataTypes.INTN0 ||
      data.value.mwDataType?.name === MWDataTypes.FLOAT ||
      data.value.mwDataType?.name === MWDataTypes.FLOATN ||
      data.value.mwDataType?.name === MWDataTypes.FLOATN0
    );
  return false;
}
</script>
